import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiSearch, 
  FiUserCheck, 
  FiClock, 
  FiShield, 
  FiAlertCircle,
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiMapPin,
  FiGlobe,
  FiHeart,
  FiBriefcase,
  FiUsers,
  FiCheckCircle,
  FiLock,
  FiUnlock,
  FiCreditCard,
  FiFileText,
  FiCamera,
  FiImage
} from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE_URL = 'https://api.liberianpost.com';

export default function Verify() {
  const [dssn, setDssn] = useState('');
  const [loading, setLoading] = useState(false);
  const [consentId, setConsentId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [userData, setUserData] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);

  const handleSearch = async () => {
    if (!dssn.trim()) {
      toast.error('Please enter a valid DSSN');
      return;
    }

    setLoading(true);
    setStatus('waiting_consent');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/citizens/request-consent?dssn=${dssn}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data.consent_required) {
        setConsentId(response.data.data.consent_id);
        toast.success('Consent request sent to user\'s device');
        pollForConsent(response.data.data.consent_id);
      } else {
        setStatus('error');
        toast.error('User not found or cannot receive notifications');
      }
    } catch (error) {
      console.error('Search error:', error);
      setStatus('error');
      toast.error(error.response?.data?.error || 'Failed to send consent request');
    }
    setLoading(false);
  };

  const pollForConsent = async (consentId) => {
    let attempts = 0;
    const maxAttempts = 30;

    const interval = setInterval(async () => {
      attempts++;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/citizens/view?dssn=${dssn}&consent_id=${consentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success && response.data.data.user) {
          clearInterval(interval);
          setUserData(response.data.data.user);
          setStatus('verified');
          toast.success('Identity verified successfully!');
        }
      } catch (error) {
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setStatus('idle');
          toast.error('Consent timeout. Please try again.');
        }
      }
    }, 2000);
  };

  const resetVerification = () => {
    setStatus('idle');
    setConsentId(null);
    setUserData(null);
    setDssn('');
    setViewingImage(null);
  };

  // Document sections for modal
  const documentSections = [
    { 
      title: 'Passport', 
      imageUrl: userData?.passport_image_url, 
      documentNumber: userData?.PASSPORT_NUMBER,
      icon: <FiFileText />
    },
    { 
      title: 'Birth Certificate', 
      imageUrl: userData?.birth_certificate_image_url, 
      documentNumber: userData?.BIRTH_CERTIFICATE,
      icon: <FiFileText />
    },
    { 
      title: "Driver's License", 
      imageUrl: userData?.drivers_license_image_url, 
      documentNumber: userData?.DRIVER_LICENSE,
      icon: <FiFileText />
    },
    { 
      title: 'National ID', 
      imageUrl: userData?.national_id_image_url, 
      documentNumber: null,
      icon: <FiFileText />
    },
  ];

  // Image Viewer Component
  const ImageViewer = ({ src, alt, onClose }) => {
    if (!src) return null;
    return (
      <div className="image-viewer-overlay" onClick={onClose}>
        <div className="image-viewer-content" onClick={(e) => e.stopPropagation()}>
          <img src={src} alt={alt} />
          <button className="image-viewer-close" onClick={onClose}>×</button>
        </div>
      </div>
    );
  };

  return (
    <div className="verify-page">
      {viewingImage && (
        <ImageViewer 
          src={viewingImage.src} 
          alt={viewingImage.alt} 
          onClose={() => setViewingImage(null)} 
        />
      )}

      {/* AI Background */}
      <div className="verify-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      <div className="verify-container">
        {/* Page Header */}
        <div className="page-header glass">
          <div className="header-content">
            <div className="title-section">
              <div className="badge">
                <HiOutlineSparkles />
                <span>Identity Verification</span>
              </div>
              <h1>Citizen Verification</h1>
              <p>Securely verify citizen identities with real-time consent</p>
            </div>
            <div className="header-stats">
              <div className="mini-stat">
                <FiShield />
                <span>GDPR Compliant</span>
              </div>
              <div className="mini-stat">
                <FiLock />
                <span>End-to-End Encrypted</span>
              </div>
            </div>
          </div>
        </div>

        {/* Search Card */}
        <div className="search-card glass">
          <div className="search-title">
            <FiSearch />
            <span>Enter DSSN to Verify</span>
          </div>
          <div className="search-input-wrapper">
            <input
              type="text"
              value={dssn}
              onChange={(e) => setDssn(e.target.value.toUpperCase())}
              className="search-input"
              placeholder="DLR-XXXX-XXXX-XXXX"
              disabled={status === 'waiting_consent'}
            />
            <button
              onClick={handleSearch}
              disabled={loading || status === 'waiting_consent'}
              className="search-button"
            >
              {loading ? (
                <div className="spinner"></div>
              ) : (
                <>
                  <FiSearch />
                  <span>Verify Identity</span>
                </>
              )}
            </button>
          </div>
          <p className="search-hint">
            <FiShield />
            Request will be sent to citizen's mobile device for approval
          </p>
        </div>

        {/* Status Display */}
        <AnimatePresence>
          {status === 'waiting_consent' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="status-card waiting glass"
            >
              <div className="status-icon waiting-icon">
                <FiClock />
              </div>
              <h3>Awaiting User Consent</h3>
              <p>A notification has been sent to the citizen's mobile device. They need to approve the verification request.</p>
              <div className="status-footer">
                <div className="pulse-dot"></div>
                <span>Waiting for approval...</span>
              </div>
            </motion.div>
          )}

          {status === 'verified' && userData && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="results-container"
            >
              {/* Success Banner */}
              <div className="success-banner glass">
                <div className="success-icon">
                  <FiCheckCircle />
                </div>
                <div className="success-text">
                  <h3>Identity Verified!</h3>
                  <p>The citizen has approved sharing their information</p>
                </div>
              </div>

              {/* Profile Summary */}
              <div className="profile-summary glass">
                {userData.image_url ? (
                  <img 
                    src={userData.image_url} 
                    alt={`${userData.first_name} ${userData.last_name}`}
                    className="profile-avatar-img"
                    onClick={() => setViewingImage({ src: userData.image_url, alt: `${userData.first_name} ${userData.last_name}` })}
                  />
                ) : (
                  <div className="profile-avatar" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                    {userData.first_name?.[0]}{userData.last_name?.[0]}
                  </div>
                )}
                <div className="profile-summary-info">
                  <h3>{userData.first_name} {userData.last_name}</h3>
                  <p><FiMail /> {userData.email || 'Not provided'}</p>
                  <p><FiPhone /> {userData.phone || 'Not provided'}</p>
                </div>
                <div className="profile-status verified">
                  <FiCheckCircle />
                  <span>Verified</span>
                </div>
              </div>

              {/* Details Grid */}
              <div className="details-grid">
                {/* Personal Information */}
                <div className="detail-card glass">
                  <h3><FiUser /> Personal Information</h3>
                  <div className="detail-row">
                    <span className="label">Full Name</span>
                    <span className="value">{userData.first_name} {userData.last_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">DSSN</span>
                    <span className="value dssn-value">{userData.dssn}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Sex</span>
                    <span className="value">{userData.SEX || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Date of Birth</span>
                    <span className="value">{userData.DATE_OF_BIRTH ? new Date(userData.DATE_OF_BIRTH).toLocaleDateString() : '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Place of Birth</span>
                    <span className="value">{userData.PLACE_OF_BIRTH || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Nationality</span>
                    <span className="value">{userData.NATIONALITY || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Marital Status</span>
                    <span className="value">{userData.MARITAL_STATUS || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Employment Status</span>
                    <span className="value">{userData.EMPLOYMENT_STATUS || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Children</span>
                    <span className="value">{userData.NUMBER_OF_CHILDREN || '—'}</span>
                  </div>
                </div>

                {/* Address Information */}
                <div className="detail-card glass">
                  <h3><FiMapPin /> Address Information</h3>
                  <div className="detail-row">
                    <span className="label">Address</span>
                    <span className="value">{userData.address || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Postal Address</span>
                    <span className="value">{userData.postal_address || '—'}</span>
                  </div>
                </div>

                {/* Account Information */}
                <div className="detail-card glass">
                  <h3><FiCalendar /> Account Information</h3>
                  <div className="detail-row">
                    <span className="label">Member Since</span>
                    <span className="value">{userData.created_at ? new Date(userData.created_at).toLocaleDateString() : '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">KYC Status</span>
                    <span className={`kyc-status ${userData.is_verified === 'approved' ? 'approved' : 'pending'}`}>
                      {userData.is_verified === 'approved' ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Account Status</span>
                    <span className={`account-status ${userData.is_active ? 'active' : 'inactive'}`}>
                      {userData.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Wallet Information */}
                {userData.wallet && (
                  <div className="detail-card glass">
                    <h3><FiCreditCard /> Wallet Information</h3>
                    <div className="detail-row">
                      <span className="label">USD Balance</span>
                      <span className="value usd-balance">${userData.wallet.balance?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Wallet ID</span>
                      <span className="value wallet-id">{userData.wallet.wallet_id || '—'}</span>
                    </div>
                  </div>
                )}

                {/* Document Information */}
                {documentSections.map((doc, idx) => (
                  (doc.imageUrl || doc.documentNumber) && (
                    <div className="detail-card glass" key={idx}>
                      <h3>{doc.icon} {doc.title}</h3>
                      {doc.documentNumber && (
                        <div className="detail-row">
                          <span className="label">Document Number</span>
                          <span className="value">{doc.documentNumber}</span>
                        </div>
                      )}
                      {doc.imageUrl && (
                        <div className="document-row">
                          <div className="document-thumbnail" onClick={() => setViewingImage({ src: doc.imageUrl, alt: doc.title })}>
                            <img src={doc.imageUrl} alt={doc.title} />
                            <div className="thumbnail-overlay">
                              <FiCamera />
                              <span>Click to view</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                ))}
              </div>

              {/* Action Button */}
              <div className="action-footer">
                <button onClick={resetVerification} className="verify-another-btn">
                  <FiSearch />
                  Verify Another Citizen
                </button>
              </div>
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="status-card error glass"
            >
              <div className="status-icon error-icon">
                <FiAlertCircle />
              </div>
              <h3>Verification Failed</h3>
              <p>Could not complete the verification. Please try again.</p>
              <button onClick={resetVerification} className="retry-btn">
                Try Again
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style jsx>{`
        .verify-page {
          min-height: 100vh;
          position: relative;
        }

        .verify-background {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
          overflow: hidden;
        }

        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.4;
        }

        .orb-1 {
          top: -20%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(102, 126, 234, 0.4), transparent);
        }

        .orb-2 {
          bottom: -20%;
          left: -10%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(168, 85, 247, 0.3), transparent);
        }

        .orb-3 {
          top: 40%;
          left: 30%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.2), transparent);
        }

        .grid-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
          background-size: 50px 50px;
        }

        .verify-container {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px 32px;
        }

        .glass {
          background: rgba(20, 20, 35, 0.6);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
        }

        /* Page Header */
        .page-header {
          padding: 28px 32px;
          margin-bottom: 32px;
          border-radius: 28px;
          background: linear-gradient(135deg, rgba(20, 20, 35, 0.7), rgba(20, 20, 35, 0.5));
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 20px;
        }

        .title-section .badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(102, 126, 234, 0.15);
          padding: 6px 14px;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 16px;
          border: 1px solid rgba(102, 126, 234, 0.3);
          color: #a8b3ff;
        }

        .title-section h1 {
          font-size: 36px;
          font-weight: 700;
          background: linear-gradient(135deg, #fff, #a8b3ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 8px;
        }

        .title-section p {
          color: rgba(255, 255, 255, 0.5);
          font-size: 14px;
        }

        .header-stats {
          display: flex;
          gap: 16px;
        }

        .mini-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 40px;
          font-size: 13px;
          backdrop-filter: blur(4px);
        }

        /* Search Card */
        .search-card {
          padding: 32px;
          margin-bottom: 32px;
          text-align: center;
        }

        .search-title {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(102, 126, 234, 0.15);
          padding: 6px 16px;
          border-radius: 40px;
          font-size: 13px;
          margin-bottom: 24px;
          color: #a8b3ff;
        }

        .search-input-wrapper {
          display: flex;
          gap: 16px;
          max-width: 600px;
          margin: 0 auto 20px;
        }

        .search-input {
          flex: 1;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 14px 20px;
          font-size: 14px;
          font-family: monospace;
          color: white;
          outline: none;
          transition: all 0.2s;
        }

        .search-input:focus {
          border-color: #667eea;
          background: rgba(255, 255, 255, 0.08);
        }

        .search-input::placeholder {
          color: rgba(255, 255, 255, 0.3);
          font-family: monospace;
        }

        .search-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          border: none;
          border-radius: 16px;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .search-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        .search-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .search-hint {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.4);
          margin-top: 16px;
        }

        /* Status Card */
        .status-card {
          padding: 48px;
          text-align: center;
          max-width: 500px;
          margin: 0 auto;
        }

        .status-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin: 0 auto 24px;
        }

        .waiting-icon {
          background: rgba(102, 126, 234, 0.15);
          color: #667eea;
        }

        .error-icon {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .status-card h3 {
          font-size: 24px;
          margin-bottom: 12px;
        }

        .status-card p {
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: 24px;
        }

        .status-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.5);
        }

        .pulse-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #667eea;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        .retry-btn {
          padding: 12px 28px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .retry-btn:hover {
          background: rgba(255, 255, 255, 0.12);
        }

        /* Results Container */
        .results-container {
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        /* Success Banner */
        .success-banner {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px 28px;
          margin-bottom: 24px;
          background: rgba(16, 185, 129, 0.1);
          border-left: 4px solid #10b981;
        }

        .success-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(16, 185, 129, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: #10b981;
        }

        .success-text h3 {
          font-size: 18px;
          margin-bottom: 4px;
          color: #10b981;
        }

        .success-text p {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
        }

        /* Profile Summary */
        .profile-summary {
          display: flex;
          align-items: center;
          gap: 24px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .profile-avatar {
          width: 80px;
          height: 80px;
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          font-weight: 600;
        }

        .profile-avatar-img {
          width: 80px;
          height: 80px;
          border-radius: 24px;
          object-fit: cover;
          cursor: pointer;
          transition: transform 0.2s;
        }

        .profile-avatar-img:hover {
          transform: scale(1.05);
        }

        .profile-summary-info h3 {
          font-size: 20px;
          margin-bottom: 8px;
        }

        .profile-summary-info p {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
          margin-top: 6px;
        }

        .profile-status {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 40px;
          font-size: 13px;
          font-weight: 500;
        }

        .profile-status.verified {
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: #10b981;
        }

        /* Details Grid */
        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .detail-card {
          padding: 24px;
        }

        .detail-card h3 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .detail-row .label {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.5);
        }

        .detail-row .value {
          font-size: 14px;
          font-weight: 500;
        }

        .dssn-value {
          color: #a855f7;
          font-family: monospace;
          letter-spacing: 0.5px;
        }

        .usd-balance {
          color: #10b981;
          font-weight: 600;
        }

        .wallet-id {
          font-family: monospace;
          font-size: 11px;
        }

        .kyc-status {
          padding: 4px 12px;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
        }

        .kyc-status.approved {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .kyc-status.pending {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .account-status {
          padding: 4px 12px;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
        }

        .account-status.active {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .account-status.inactive {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        /* Document Styles */
        .document-row {
          margin-top: 12px;
        }

        .document-thumbnail {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: transform 0.2s;
        }

        .document-thumbnail:hover {
          transform: scale(1.02);
        }

        .document-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .thumbnail-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          opacity: 0;
          transition: opacity 0.2s;
          color: white;
          font-size: 10px;
        }

        .document-thumbnail:hover .thumbnail-overlay {
          opacity: 1;
        }

        /* Action Footer */
        .action-footer {
          text-align: center;
          margin-top: 24px;
        }

        .verify-another-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 32px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          color: white;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .verify-another-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
        }

        /* Image Viewer */
        .image-viewer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          backdrop-filter: blur(20px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }

        .image-viewer-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
        }

        .image-viewer-content img {
          max-width: 90vw;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 16px;
        }

        .image-viewer-close {
          position: absolute;
          top: -40px;
          right: -40px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          font-size: 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 768px) {
          .verify-container {
            padding: 16px;
          }
          
          .title-section h1 {
            font-size: 24px;
          }
          
          .search-input-wrapper {
            flex-direction: column;
          }
          
          .profile-summary {
            flex-direction: column;
            text-align: center;
          }
          
          .profile-status {
            margin-left: 0;
          }
          
          .details-grid {
            grid-template-columns: 1fr;
          }
          
          .image-viewer-close {
            top: -50px;
            right: 0;
          }
        }
      `}</style>
    </div>
  );
}
